import React, { useState, useEffect, useRef } from 'react';
import {
    Typography,
    Card,
    Button,
    Space,
    Form,
    Input,
    DatePicker,
    Switch,
    InputNumber,
    Alert,
    Divider,
    Row,
    Col,
    Modal,
    Progress,
    Radio,
} from 'antd';
import {
    ArrowLeftOutlined,
    CopyOutlined,
    SaveOutlined,
    LoadingOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { TrainingType } from '../types/training';
import {
    getMoodleProgramDetails,
    getMoodleQuizQuestions,
    createMoodleProgram,
    createMoodleQuiz,
    updateMoodleProgram,
} from '../services/moodleService';
import { validateBlobSafeName, parseModuleId } from '../utils/moduleUtils';
import { getCurrentUserEmail } from '../services/trainingService';

const { Title, Text } = Typography;
const { TextArea } = Input;

const stripHtml = (html: string) =>
    html
        .replace(/<br\s*\/?>\s*<pre[^>]*>[\s\S]*?<\/pre>/gi, '')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .trim();

const mapMoodleQuestion = (q: any, idx: number) => {
    let codeSnippet = '';
    let language = 'javascript';
    try {
        const fb = JSON.parse(q.generalfeedback || '{}');
        codeSnippet = fb.codeSnippet || fb.CodeSnippet || '';
        language = fb.language || fb.Language || 'javascript';
    } catch { /* ignore parse errors */ }
    return {
        id: String(q.id || idx),
        question: stripHtml(q.questiontext || q.name || ''),
        answer: stripHtml(q.graderinfo || ''),
        codeSnippet,
        language,
    };
};

interface ClonedModule {
    id: string;
    name: string;
    estimatedTime: number;
    questions: any[];
    _moodleId?: number;
    _description?: string;
    _attemptLimit?: number;
}

const CloneProgram: React.FC = () => {
    const { trainingId } = useParams<{ trainingId: string }>();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const publishIntentRef = useRef(false);

    const [pageLoading, setPageLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState('Loading program details...');
    const [loadError, setLoadError] = useState('');
    const [sourceProgram, setSourceProgram] = useState<any>(null);
    const [modules, setModules] = useState<ClonedModule[]>([]);
    const [submitting, setSubmitting] = useState(false);

    const assessmentsHierarchyRef = useRef<any[]>([]);

    const [progressVisible, setProgressVisible] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [totalSteps, setTotalSteps] = useState(0);
    const [progressMessage, setProgressMessage] = useState('');
    const [processStatus, setProcessStatus] = useState<'active' | 'success' | 'exception'>('active');

    useEffect(() => {
        if (trainingId) loadSourceProgram(trainingId);
    }, [trainingId]);

    const loadSourceProgram = async (tid: string) => {
        try {
            setPageLoading(true);
            setLoadError('');

            const program = await getMoodleProgramDetails(tid);
            if (!program) throw new Error('Program not found');
            setSourceProgram(program);

            let flatModules: ClonedModule[] = [];

            if (program.programType === 'AINested') {
                const subjects: any[] = JSON.parse(program.assessments || '[]');
                assessmentsHierarchyRef.current = subjects;

                const rawModules = subjects.flatMap((s: any, si: number) =>
                    (s.chapters || []).flatMap((c: any, ci: number) =>
                        (c.modules || []).map((m: any, mi: number) => ({
                            id: m.id || `s${si}c${ci}m${mi}`,
                            name: m.name,
                            estimatedTime: m.estimatedTime || 30,
                            questions: [],
                            _moodleId: m.moodleId,
                            _description: m.description || '',
                            _attemptLimit: m.attemptLimit || 0,
                        }))
                    )
                );

                flatModules = [];
                for (let i = 0; i < rawModules.length; i++) {
                    const mod = rawModules[i];
                    setLoadingMessage(`Loading questions: ${mod.name} (${i + 1}/${rawModules.length})...`);
                    try {
                        const result = await getMoodleQuizQuestions(program.courseId, mod.name, mod._moodleId || 0);
                        flatModules.push({ ...mod, questions: result.questions.map(mapMoodleQuestion) });
                    } catch { flatModules.push(mod); }
                }
            } else {
                const rawModules = (program.assessments || '')
                    .split('|')
                    .map((s: string) => s.trim())
                    .filter(Boolean)
                    .map((raw: string, idx: number) => {
                        const { name, description, estimatedTime, attemptLimit } = parseModuleId(raw);
                        return { id: `m${idx}`, name, estimatedTime, questions: [], _description: description, _attemptLimit: attemptLimit };
                    });

                flatModules = [];
                for (let i = 0; i < rawModules.length; i++) {
                    const mod = rawModules[i];
                    setLoadingMessage(`Loading questions: ${mod.name} (${i + 1}/${rawModules.length})...`);
                    try {
                        const result = await getMoodleQuizQuestions(program.courseId, mod.name, 0);
                        flatModules.push({ ...mod, questions: result.questions.map(mapMoodleQuestion) });
                    } catch { flatModules.push(mod); }
                }
            }

            setModules(flatModules);

            form.setFieldsValue({
                trainingId: '',
                trainingName: `Copy of ${program.trainingName}`,
                passingPercentage: program.passingPercentage,
                estimatedEffortHours: program.estimatedEffortHours,
                trainingLink: program.trainingLink || '',
                trainingType: program.trainingType || TrainingType.OPEN,
                isEvergreen: true,
                audienceEmails: '',
                stakeholderEmails: '',
            });
        } catch (err: any) {
            setLoadError(err.message || 'Failed to load source program');
        } finally {
            setPageLoading(false);
        }
    };

    const handleFinish = async (values: any) => {
        try {
            setTotalSteps(modules.length + 1);
            setCurrentStep(0);
            setProcessStatus('active');
            setProgressMessage('Creating program in Moodle...');
            setProgressVisible(true);
            setSubmitting(true);

            const cleanEmails = (str: string) =>
                str ? str.split(/[\n,;]+/).map((e: string) => e.trim()).filter(Boolean).join(';') : '';

            const isEvergreen = values.isEvergreen && values.trainingType === TrainingType.OPEN;
            const startDate = isEvergreen ? '' : values.dateRange[0].format('YYYY-MM-DD');
            const endDate   = isEvergreen ? '' : values.dateRange[1].format('YYYY-MM-DD');

            const programType = sourceProgram.programType || 'AI';
            const isNested = programType === 'AINested';

            const aiAssessmentsStr = isNested
                ? ''
                : modules.map(m => {
                    let entry = m.name.trim();
                    if (m._description) entry += ` [${m._description}]`;
                    entry += ` #T${m.estimatedTime || 30}`;
                    if (m._attemptLimit) entry += ` #A${m._attemptLimit}`;
                    return entry;
                }).join(' | ');

            const programResult = await createMoodleProgram({
                trainingId: values.trainingId,
                trainingName: values.trainingName,
                passingPercentage: values.passingPercentage,
                estimatedEffortHours: values.estimatedEffortHours,
                trainingLink: values.trainingLink || '',
                trainingType: values.trainingType,
                programType,
                audienceEmails: cleanEmails(values.audienceEmails || ''),
                stakeholderEmails: cleanEmails(values.stakeholderEmails || ''),
                assessments: aiAssessmentsStr,
                startDate,
                endDate,
                visible: 0,
                createdby: await getCurrentUserEmail(),
            });

            const courseId = programResult.id;
            setCurrentStep(1);

            if (isNested) {
                const newMoodleIdByModuleId: Record<string, number> = {};

                for (let i = 0; i < modules.length; i++) {
                    const mod = modules[i];
                    setProgressMessage(`Creating quiz: ${mod.name} (${i + 1}/${modules.length})...`);
                    const quizResult = await createMoodleQuiz(
                        courseId, mod.name,
                        mod._description || `Assessment for ${mod.name}.`,
                        i + 1, mod.questions, mod.estimatedTime || 30, mod._attemptLimit || 0
                    );
                    newMoodleIdByModuleId[mod.id] = quizResult?.id || quizResult?.quizid || 0;
                    setCurrentStep(prev => prev + 1);
                }

                const newHierarchy = assessmentsHierarchyRef.current.map(s => ({
                    ...s,
                    chapters: (s.chapters || []).map((c: any) => ({
                        ...c,
                        modules: (c.modules || []).map((m: any) => ({
                            ...m,
                            moodleId: newMoodleIdByModuleId[m.id] || m.moodleId,
                            deadline: 0,
                        })),
                    })),
                }));

                await updateMoodleProgram({
                    id: courseId,
                    trainingName: values.trainingName,
                    passingPercentage: values.passingPercentage,
                    estimatedEffortHours: values.estimatedEffortHours,
                    trainingType: values.trainingType,
                    audienceEmails: cleanEmails(values.audienceEmails || ''),
                    stakeholderEmails: cleanEmails(values.stakeholderEmails || ''),
                    trainingLink: values.trainingLink || '',
                    startDate,
                    endDate,
                    programType,
                    assessments: JSON.stringify(newHierarchy),
                });
            } else {
                for (let i = 0; i < modules.length; i++) {
                    const mod = modules[i];
                    setProgressMessage(`Creating quiz: ${mod.name} (${i + 1}/${modules.length})...`);
                    await createMoodleQuiz(
                        courseId, mod.name, `Assessment for ${mod.name}.`,
                        i + 1, mod.questions, mod.estimatedTime || 30
                    );
                    setCurrentStep(prev => prev + 1);
                }
            }

            setProcessStatus('success');
            setProgressMessage('Program cloned as draft. Open it in Edit mode to make changes, then publish.');
            setTimeout(() => navigate('/admin'), 2000);
        } catch (err: any) {
            setProcessStatus('exception');
            setProgressMessage(`Error: ${err.message || 'Clone failed'}`);
            setSubmitting(false);
        }
    };

    const percent = Math.round((currentStep / Math.max(totalSteps, 1)) * 100);
    const totalQuestions = modules.reduce((sum, m) => sum + m.questions.length, 0);

    if (pageLoading) {
        return (
            <div style={{ padding: 40, textAlign: 'center' }}>
                <LoadingOutlined style={{ fontSize: 24, marginBottom: 12 }} />
                <div>{loadingMessage}</div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div style={{ padding: 40 }}>
                <Alert type="error" message={loadError} />
                <Button onClick={() => navigate('/admin')} style={{ marginTop: 16 }}>Back to Admin</Button>
            </div>
        );
    }

    return (
        <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>

            <div style={{ marginBottom: '24px' }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin')} style={{ marginBottom: '16px' }}>
                    Back to Admin
                </Button>
                <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{
                            width: 64, height: 64, borderRadius: '16px',
                            backgroundColor: '#f6ffed',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            marginRight: 24,
                        }}>
                            <CopyOutlined style={{ fontSize: '32px', color: '#52c41a' }} />
                        </div>
                        <div>
                            <Title level={2} style={{ margin: 0 }}>Clone Training Program</Title>
                            <Text type="secondary">
                                Cloning: <strong>{sourceProgram?.trainingName}</strong> ({sourceProgram?.programType})
                            </Text>
                        </div>
                    </div>
                </Card>
            </div>

            <Alert
                style={{ marginBottom: 24, borderRadius: 8 }}
                type="info"
                showIcon
                message={`All ${modules.length} module(s) and ${totalQuestions} question(s) will be cloned from the source program.`}
                description="To edit questions or modules after cloning, open the new program in Edit mode. Once you're satisfied, publish it from there."
            />

            <Form form={form} layout="vertical" onFinish={handleFinish}
                initialValues={{ passingPercentage: 80, trainingType: TrainingType.OPEN, isEvergreen: true }}
            >
                <Card title="Program Details"
                    style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
                >
                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label="Training ID" name="trainingId"
                                rules={[{ required: true }, { validator: validateBlobSafeName }]}
                                extra="Must be unique — different from the source program."
                            >
                                <Input placeholder="TR-202X-001" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Passing %" name="passingPercentage" rules={[{ required: true }]}>
                                <InputNumber min={0} max={100} style={{ width: '100%' }}
                                    formatter={value => value ? `${value}%` : ''}
                                    parser={value => value ? parseFloat(value.replace('%', '')) : 0 as any}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Assessment Duration (Hours)" name="estimatedEffortHours">
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item label="Training Program Name" name="trainingName" rules={[{ required: true }]}>
                        <Input size="large" maxLength={250} showCount />
                    </Form.Item>

                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item noStyle shouldUpdate={(prev, cur) =>
                                prev.trainingType !== cur.trainingType || prev.isEvergreen !== cur.isEvergreen
                            }>
                                {({ getFieldValue, setFieldsValue }) => {
                                    const trainingType = getFieldValue('trainingType');
                                    const isEvergreen = getFieldValue('isEvergreen');
                                    if (trainingType !== TrainingType.OPEN && isEvergreen) {
                                        setTimeout(() => setFieldsValue({ isEvergreen: false }), 0);
                                    }
                                    return (
                                        <>
                                            {trainingType === TrainingType.OPEN && (
                                                <Form.Item name="isEvergreen" valuePropName="checked" style={{ marginBottom: 16 }}>
                                                    <Switch checkedChildren="Evergreen (No Fixed Dates)" unCheckedChildren="Fixed Dates" />
                                                </Form.Item>
                                            )}
                                            {(!isEvergreen || trainingType !== TrainingType.OPEN) ? (
                                                <Form.Item label="Training Period" name="dateRange"
                                                    rules={[{ required: true, message: 'Please select a training period' }]}
                                                    extra="Users can only start training on or after the start date."
                                                >
                                                    <DatePicker.RangePicker style={{ width: '100%' }} size="large" />
                                                </Form.Item>
                                            ) : (
                                                <Alert message="Evergreen Program"
                                                    description="This training has no fixed dates and will be available immediately."
                                                    type="info" showIcon style={{ marginBottom: 24 }}
                                                />
                                            )}
                                        </>
                                    );
                                }}
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Resource / Documentation Link" name="trainingLink" rules={[{ required: true }]}>
                                <TextArea autoSize={{ minRows: 2, maxRows: 6 }} placeholder="https://..." style={{ borderRadius: '8px' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider />

                    <Form.Item label="Training Accessibility Type" name="trainingType" rules={[{ required: true }]}>
                        <Radio.Group>
                            <Space direction="vertical">
                                <Radio value={TrainingType.OPEN}>
                                    <Text strong>Open for All</Text><br />
                                    <Text type="secondary">No nominations required. Anyone can join and take the assessment anytime.</Text>
                                </Radio>
                                <Radio value={TrainingType.REQUESTABLE}>
                                    <Text strong>Nominated + Request to Join</Text><br />
                                    <Text type="secondary">Specific people are nominated. Others can request to join before the start date.</Text>
                                </Radio>
                                <Radio value={TrainingType.PRIVATE}>
                                    <Text strong>Nominated Only (Private)</Text><br />
                                    <Text type="secondary">Only specific people nominated by the admin can access this training.</Text>
                                </Radio>
                            </Space>
                        </Radio.Group>
                    </Form.Item>

                    <Divider />

                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.trainingType !== cur.trainingType}>
                        {({ getFieldValue }) => {
                            if (getFieldValue('trainingType') === TrainingType.OPEN) return null;
                            return (
                                <Row gutter={[24, 0]}>
                                    <Col xs={24} md={12}>
                                        <Form.Item label="Nominated Participants (Emails, semicolon separated)"
                                            name="audienceEmails"
                                            rules={[{ required: true, message: 'Participant emails are required' }]}
                                        >
                                            <TextArea rows={4} placeholder="user1@company.com; user2@company.com" style={{ borderRadius: '8px' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item label="Stakeholders (Emails, semicolon separated)"
                                            name="stakeholderEmails"
                                            rules={[{ required: true, message: 'Stakeholder emails are mandatory' }]}
                                        >
                                            <TextArea rows={4} placeholder="manager@company.com; director@company.com" style={{ borderRadius: '8px' }} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            );
                        }}
                    </Form.Item>
                </Card>

                <div style={{ textAlign: 'right', marginTop: '32px', marginBottom: '50px' }}>
                    <Space size="large">
                        <Button onClick={() => navigate('/admin')} size="large" disabled={submitting}>Cancel</Button>
                        <Button type="primary" size="large" icon={<SaveOutlined />} loading={submitting}
                            onClick={() => { publishIntentRef.current = false; form.submit(); }}
                        >
                            Save as Draft
                        </Button>
                    </Space>
                </div>
            </Form>

            <Modal
                title={<Space>{processStatus === 'active' && <LoadingOutlined />}<span>Cloning Program</span></Space>}
                open={progressVisible}
                closable={processStatus === 'exception'}
                maskClosable={false}
                onCancel={() => { if (processStatus === 'exception') setProgressVisible(false); }}
                footer={processStatus === 'exception' ? [
                    <Button key="close" onClick={() => setProgressVisible(false)}>Close</Button>
                ] : null}
                centered
            >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Progress type="circle" percent={percent} status={processStatus} strokeColor="#52c41a" />
                    <div style={{ marginTop: '24px' }}>
                        <Title level={4}>{processStatus === 'success' ? 'Done!' : 'Creating...'}</Title>
                        <Text>{progressMessage}</Text>
                    </div>
                    {processStatus === 'active' && (
                        <Alert message="Do not close this window"
                            description="Creating quizzes in Moodle may take a moment."
                            type="warning" showIcon style={{ marginTop: '24px', textAlign: 'left' }}
                        />
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default CloneProgram;
